import type { Node as PMNode } from '@tiptap/pm/model'

// Sentence-level analysis of prose for Editor Mode: where each sentence
// starts and ends, how long it is, and the word it opens with. Everything is
// measured in document positions so the editor can decorate and jump to it.

export interface SentenceInfo {
  /** Position in the whole document's sentence order. */
  index: number
  paragraphIndex: number
  indexInParagraph: number
  /** Document positions of the sentence, without surrounding whitespace. */
  from: number
  to: number
  text: string
  words: number
  /** First word, lower-cased, curly apostrophes straightened; '' if none. */
  opener: string
  /** The first word as written. */
  openerText: string
  openerFrom: number
  openerTo: number
  /** First two words, lower-cased. */
  openerPair: string
  /** The sentence begins with a quotation mark. */
  dialogue: boolean
}

export type ParagraphKind = 'paragraph' | 'heading' | 'rule'

export interface ParagraphInfo {
  index: number
  kind: ParagraphKind
  /** Document position of the first character of the block's text. */
  from: number
  to: number
  /** The block's text; character i sits at document position from + i. */
  text: string
  sentences: SentenceInfo[]
}

export interface ProseAnalysis {
  paragraphs: ParagraphInfo[]
  /** Every sentence in prose paragraphs (headings excluded), in order. */
  sentences: SentenceInfo[]
  /** Sentence openers outside dialogue → how many sentences start with them. */
  openerCounts: Map<string, number>
  /** Sentences counted in openerCounts. */
  narrativeSentenceCount: number
}

// Placeholder for inline nodes that are not text (images, loose-end pins…),
// and for the open/close boundary of inline nodes with content. Each stands
// for exactly one document position, so string index ↔ position stays exact.
const OBJECT_CHAR = '￼'

const WORD_RE = /[\p{L}\p{N}]+(?:['’\-][\p{L}\p{N}]+)*/gu
const QUOTE_START_RE = /^[\s(\[]*["“‘'«]/

/** Abbreviations whose trailing period does not end a sentence. */
const ABBREVIATIONS = new Set([
  'mr', 'mrs', 'ms', 'mx', 'dr', 'prof', 'sr', 'jr', 'st', 'mt', 'ft', 'vs', 'etc', 'vol', 'inc', 'ltd', 'corp',
  'gen', 'col', 'capt', 'lt', 'sgt', 'cpl', 'maj', 'adm', 'cmdr', 'gov', 'sen', 'rev', 'hon', 'pres', 'supt',
  'insp', 'approx', 'dept', 'jan', 'feb', 'apr', 'jun', 'jul', 'aug', 'sept', 'oct', 'nov', 'dec', 'tues',
  'thur', 'thurs', 'e.g', 'i.e', 'cf', 'ave', 'blvd', 'pp',
])

/** A capitalized speaker tag after a quotation: “Why?” Tom asked. */
const TAG_AFTER_QUOTE_RE = /^\s+\p{Lu}[\p{L}'’\-]*(?:\s+\p{Lu}[\p{L}'’\-]*)?\s+(?:said|asked|replied|answered|whispered|shouted|called|cried|muttered|murmured|added|says|asks|snapped|yelled)\b/u

export function countWords(text: string): number {
  return text.match(WORD_RE)?.length ?? 0
}

function normalizeWord(word: string): string {
  return word.toLowerCase().replace(/’/g, "'")
}

/** Character ranges of each sentence in `text`, trimmed of whitespace. */
export function splitSentences(text: string): { start: number; end: number }[] {
  const ranges: { start: number; end: number }[] = []
  let start = 0

  const push = (end: number) => {
    let s = start
    let e = end
    while (s < e && /\s|￼/.test(text[s])) s++
    while (e > s && /\s/.test(text[e - 1])) e--
    if (e > s && /[\p{L}\p{N}]/u.test(text.slice(s, e))) ranges.push({ start: s, end: e })
    else if (e > s && ranges.length) ranges[ranges.length - 1].end = Math.max(ranges[ranges.length - 1].end, e)
  }

  // Terminal punctuation, then any closing quotes or brackets.
  const re = /[.!?…‽]+["'”’»)\]]*|\n/g
  let match: RegExpExecArray | null
  while ((match = re.exec(text)) !== null) {
    const end = match.index + match[0].length
    if (match[0] === '\n') {
      push(match.index)
      start = end
      continue
    }
    const after = text.slice(end)
    // Punctuation inside a word or number ("3.5", "U.S.A") is not an ending.
    if (after && !/^\s/.test(after)) continue
    const next = after.match(/^\s*(.)/u)?.[1]
    if (next !== undefined) {
      // "Stop!" she said. / "Well…" he began. — a lower-case word carries on.
      if (/\p{Ll}/u.test(next)) continue
      if (/["'”’»]$/.test(match[0]) && TAG_AFTER_QUOTE_RE.test(after)) continue
      if (match[0].startsWith('.') && !match[0].startsWith('..')) {
        const before = text.slice(start, match.index).match(/([\p{L}.]+)$/u)?.[1] ?? ''
        const word = before.toLowerCase()
        if (ABBREVIATIONS.has(word) || ABBREVIATIONS.has(word.replace(/\.$/, ''))) continue
        // A lone initial: "J. R. Tolkien".
        if (/^\p{Lu}$/u.test(before)) continue
      }
    }
    push(end)
    start = end
  }
  push(text.length)
  return ranges
}

/** The text of a textblock, with one character per document position. */
function blockText(node: PMNode): string {
  let text = ''
  const walk = (parent: PMNode) => {
    parent.forEach((child) => {
      if (child.isText) text += child.text
      else if (child.type.name === 'hardBreak') text += '\n'
      else if (child.isLeaf) text += OBJECT_CHAR.repeat(child.nodeSize)
      else {
        text += OBJECT_CHAR
        walk(child)
        text += OBJECT_CHAR
      }
    })
  }
  walk(node)
  return text
}

/** Analyzes every prose block in a ProseMirror document. */
export function analyzeDoc(doc: PMNode): ProseAnalysis {
  const paragraphs: ParagraphInfo[] = []
  const sentences: SentenceInfo[] = []
  const openerCounts = new Map<string, number>()
  let narrativeSentenceCount = 0

  doc.descendants((node, pos) => {
    if (node.type.name === 'codeBlock') return false
    if (node.type.name === 'horizontalRule') {
      paragraphs.push({ index: paragraphs.length, kind: 'rule', from: pos, to: pos, text: '', sentences: [] })
      return false
    }
    if (!node.isTextblock) return true

    const text = blockText(node)
    const from = pos + 1
    const kind: ParagraphKind = node.type.name === 'heading' ? 'heading' : 'paragraph'
    const paragraph: ParagraphInfo = {
      index: paragraphs.length,
      kind,
      from,
      to: from + text.length,
      text,
      sentences: [],
    }
    paragraphs.push(paragraph)
    if (kind === 'heading' || !text.trim()) return false

    for (const range of splitSentences(text)) {
      const sentenceText = text.slice(range.start, range.end)
      WORD_RE.lastIndex = 0
      const first = WORD_RE.exec(sentenceText)
      const second = first ? WORD_RE.exec(sentenceText) : null
      WORD_RE.lastIndex = 0
      const opener = first ? normalizeWord(first[0]) : ''
      const sentence: SentenceInfo = {
        index: sentences.length,
        paragraphIndex: paragraph.index,
        indexInParagraph: paragraph.sentences.length,
        from: from + range.start,
        to: from + range.end,
        text: sentenceText,
        words: countWords(sentenceText),
        opener,
        openerText: first ? first[0] : '',
        openerFrom: first ? from + range.start + first.index : from + range.start,
        openerTo: first ? from + range.start + first.index + first[0].length : from + range.start,
        openerPair: first && second ? `${opener} ${normalizeWord(second[0])}` : opener,
        dialogue: QUOTE_START_RE.test(sentenceText),
      }
      paragraph.sentences.push(sentence)
      sentences.push(sentence)
      if (opener && !sentence.dialogue) {
        narrativeSentenceCount++
        openerCounts.set(opener, (openerCounts.get(opener) ?? 0) + 1)
      }
    }
    return false
  })

  return { paragraphs, sentences, openerCounts, narrativeSentenceCount }
}

// ── Opening patterns ─────────────────────────────────────────────────────
// Families of sentence openings that tend to become habits in a draft.

export type OpenerPatternId = 'pronoun' | 'article' | 'conjunction' | 'participle' | 'subordinate' | 'adverb'

export const OPENER_PATTERNS: { id: OpenerPatternId; label: string; hint: string }[] = [
  { id: 'pronoun', label: 'Pronouns', hint: 'I, he, she, they, it…' },
  { id: 'article', label: 'Articles', hint: 'The, a, an' },
  { id: 'conjunction', label: 'Conjunctions', hint: 'And, but, so, then…' },
  { id: 'participle', label: '-ing openings', hint: 'Walking to the door, …' },
  { id: 'subordinate', label: 'As / when clauses', hint: 'As, when, while, after…' },
  { id: 'adverb', label: '-ly adverbs', hint: 'Suddenly, slowly…' },
]

const PRONOUNS = new Set(['i', 'he', 'she', 'it', 'they', 'we', 'you', 'his', 'her', 'its', 'their', 'our', 'my', 'your', 'him', 'them', 'me', 'us'])
const ARTICLES = new Set(['the', 'a', 'an'])
const CONJUNCTIONS = new Set(['and', 'but', 'so', 'or', 'nor', 'yet', 'then', 'because'])
const SUBORDINATORS = new Set(['as', 'when', 'while', 'after', 'before', 'if', 'although', 'though', 'since', 'once', 'until', 'whenever'])
const NOT_PARTICIPLES = new Set([
  'thing', 'things', 'nothing', 'something', 'everything', 'anything', 'king', 'kings', 'morning', 'evening', 'during',
  'spring', 'string', 'wing', 'wings', 'ring', 'rings', 'sing', 'bring', 'sting', 'swing', 'ceiling', 'building',
  'darling', 'sibling', 'siblings', 'pudding', 'wedding', 'feeling', 'feelings', 'beijing', 'viking', 'vikings', 'sterling',
])
export const NOT_ADVERBS = new Set([
  'only', 'early', 'family', 'fly', 'ally', 'july', 'italy', 'holy', 'lily', 'emily', 'sally', 'reply', 'apply', 'supply',
  'belly', 'jelly', 'bully', 'rally', 'hilly', 'silly', 'ugly', 'lonely', 'lovely', 'friendly', 'daily', 'likely', 'kelly',
  'molly', 'polly', 'holly', 'billy', 'willy', 'nelly', 'shelly', 'ely', 'ply', 'rely', 'fully',
])

export function openerPattern(opener: string): OpenerPatternId | null {
  const base = opener.split("'")[0]
  if (PRONOUNS.has(base)) return 'pronoun'
  if (ARTICLES.has(opener)) return 'article'
  if (CONJUNCTIONS.has(opener)) return 'conjunction'
  if (SUBORDINATORS.has(opener)) return 'subordinate'
  if (opener.length > 4 && opener.endsWith('ing') && !NOT_PARTICIPLES.has(opener)) return 'participle'
  if (opener.length > 4 && opener.endsWith('ly') && !NOT_ADVERBS.has(opener)) return 'adverb'
  return null
}

// ── Overuse ──────────────────────────────────────────────────────────────

/** An opener is overused when it starts at least this share of sentences. */
export const OVERUSED_SHARE = 0.1
export const OVERUSED_MIN_COUNT = 3

export function isOverusedOpener(analysis: ProseAnalysis, opener: string): boolean {
  const count = analysis.openerCounts.get(opener) ?? 0
  return count >= OVERUSED_MIN_COUNT && count / Math.max(1, analysis.narrativeSentenceCount) >= OVERUSED_SHARE
}

/** Whether a sentence opens with the same word as the narrative sentence before it. */
export function repeatsPreviousOpener(analysis: ProseAnalysis, sentence: SentenceInfo): boolean {
  if (sentence.dialogue || !sentence.opener) return false
  for (let i = sentence.index - 1; i >= 0; i--) {
    const previous = analysis.sentences[i]
    if (previous.dialogue) continue
    return previous.opener === sentence.opener
  }
  return false
}

// ── Rhythm ───────────────────────────────────────────────────────────────

export interface RhythmStats {
  count: number
  mean: number
  median: number
  deviation: number
  shortest: number
  longest: number
  longCount: number
  /** Runs of three or more consecutive sentences whose lengths sit within four words of each other. */
  flatRuns: { start: number; length: number }[]
}

export function rhythmStats(sentences: SentenceInfo[], longThreshold: number): RhythmStats {
  const lengths = sentences.map((s) => s.words)
  const count = lengths.length
  if (!count) return { count: 0, mean: 0, median: 0, deviation: 0, shortest: 0, longest: 0, longCount: 0, flatRuns: [] }
  const mean = lengths.reduce((a, b) => a + b, 0) / count
  const sorted = [...lengths].sort((a, b) => a - b)
  const median = count % 2 ? sorted[(count - 1) / 2] : (sorted[count / 2 - 1] + sorted[count / 2]) / 2
  const deviation = Math.sqrt(lengths.reduce((sum, n) => sum + (n - mean) ** 2, 0) / count)

  const flatRuns: { start: number; length: number }[] = []
  let runStart = 0
  let low = lengths[0]
  let high = lengths[0]
  for (let i = 1; i <= count; i++) {
    const n = lengths[i]
    if (i < count && Math.max(high, n) - Math.min(low, n) <= 4) {
      low = Math.min(low, n)
      high = Math.max(high, n)
      continue
    }
    if (i - runStart >= 3) flatRuns.push({ start: runStart, length: i - runStart })
    runStart = i
    low = high = n
  }

  return {
    count,
    mean,
    median,
    deviation,
    shortest: sorted[0],
    longest: sorted[count - 1],
    longCount: lengths.filter((n) => n >= longThreshold).length,
    flatRuns,
  }
}

export type LengthBand = 'short' | 'medium' | 'long' | 'very-long'

export function lengthBand(words: number, longThreshold: number): LengthBand {
  if (words >= Math.round(longThreshold * 1.5)) return 'very-long'
  if (words >= longThreshold) return 'long'
  if (words <= 7) return 'short'
  return 'medium'
}
